import React from "react";

export const detectChrome = () => {
  return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
};

// Detects whether or not this is IE. Useful for polyfills and IE bugfixes.
export const detectIE = () => {
  let ua = window.navigator.userAgent;
  let msie = ua.indexOf("MSIE ");

  if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv:11\./)) {
    return true;
  } else {
    return false;
  }
};

export const detectSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

// Reveals all previously hidden content with a fade effect
export const fadeIn = () => {
  const fadeNodes = document.querySelectorAll(".js-fadeMeIn");

  if (fadeNodes.length !== 0) {
    setTimeout(() => {
      fadeNodes.forEach(node => {
        node.classList.remove("js-fadeMeIn");
      });
    }, 5);
  }
};

// Hides a node, does a thing, then unhides it based on the CSS values
// set for its transition.
// Removing the class at the end is optional in case we're running it
// again before the final fade, as we do with the lightbox
export const fadeOutThen = (node, callback, removeFadeAfter = true) => {
  // We can't simply destroy the style, as masonry layouts use inline styles.
  let nodeCachedStyle = node.attributes.style;

  node.classList.add("js-fadeOut");
  node.style.transitionDelay = "0s";

  let transitionDurationInSecs = getComputedStyle(node)["transition-duration"];
  let transitionDurationInMS = parseFloat(transitionDurationInSecs.slice(0, -1), 10) * 1000;

  setTimeout(() => {
    callback();

    if (removeFadeAfter) {
      node.classList.remove("js-fadeOut");
    }
  }, transitionDurationInMS);

  node.attributes.style = nodeCachedStyle;
};

export const scrollToTop = () => {
  document.body.scrollTop = document.documentElement.scrollTop = 0;
};

// Updates the default and social media meta in the head
export const setMeta = (type, value) => {
  if (type === "description") {
    value = value
      ? value
      : "Creators of many of the world’s most iconic and enduring brands, Chermayeff & Geismar & Haviv is an independent design firm specializing in the development of trademarks and identity programs.";

    if (document.querySelector('meta[name="description"]') === null) {
      let meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      let metaOG = document.createElement("meta");
      metaOG.setAttribute("property", "og:description");
      let metaTw = document.createElement("meta");
      metaTw.setAttribute("name", "twitter:description");

      document.getElementsByTagName("head")[0].appendChild(meta);
      document.getElementsByTagName("head")[0].appendChild(metaOG);
      document.getElementsByTagName("head")[0].appendChild(metaTw);
    }

    const descriptionArray = [
      document.querySelector('meta[name="description"]'),
      document.querySelector('meta[name="twitter:description"]'),
      document.querySelector('meta[property="og:description"]'),
    ];

    descriptionArray.map(n => n.setAttribute("content", value));
  } else if (type === "title") {
    value = value ? unescape(value) : "Chermayeff & Geismar & Haviv";

    if (document.querySelector('meta[name="twitter:title"]') === null) {
      let metaOG = document.createElement("meta");
      metaOG.setAttribute("property", "og:title");
      let metaTw = document.createElement("meta");
      metaTw.setAttribute("name", "twitter:title");

      document.getElementsByTagName("head")[0].appendChild(metaOG);
      document.getElementsByTagName("head")[0].appendChild(metaTw);
    }

    const titleArray = [
      document.querySelector('meta[name="twitter:title"]'),
      document.querySelector('meta[property="og:title"]'),
    ];

    titleArray.map(n => n.setAttribute("content", value));
    document.title = value;
  } else if (type === "image") {
    value = value ? value : `${window.location.protocol}//${window.location.hostname}/img/og.png`;

    if (document.querySelector('meta[name="twitter:image"]') === null) {
      let metaOG = document.createElement("meta");
      metaOG.setAttribute("property", "og:image");
      let metaTw = document.createElement("meta");
      metaTw.setAttribute("name", "twitter:image");

      document.getElementsByTagName("head")[0].appendChild(metaOG);
      document.getElementsByTagName("head")[0].appendChild(metaTw);
    }

    const imageArray = [
      document.querySelector('meta[name="twitter:image"]'),
      document.querySelector('meta[property="og:image"]'),
    ];

    imageArray.map(n => n.setAttribute("content", value));
  }

  if (document.querySelector('meta[name="twitter:url"]') === null) {
    let metaOG = document.createElement("meta");
    metaOG.setAttribute("property", "og:url");
    let metaTw = document.createElement("meta");
    metaTw.setAttribute("name", "twitter:url");

    document.getElementsByTagName("head")[0].appendChild(metaOG);
    document.getElementsByTagName("head")[0].appendChild(metaTw);
  }

  const urlArray = [
    document.querySelector('meta[name="twitter:url"]'),
    document.querySelector('meta[property="og:url"]'),
  ];

  if (window.location.href !== urlArray[0].getAttribute("content")) {
    urlArray.map(n => n.setAttribute("content", window.location.href));
  }
};

// make a srcSet compatible retina-ready image with all available sizes.
// We need the columns to figure out what the effective image sizes will be.
export const srcsetImage = (img, alt = img.alt, columns = 12, classes = []) => {
  var srcSetStrings = [];
  var sizesString = "";

  // If there is no image yet, drop out early
  if (img.sizes === undefined) {
    return;
  }

  // Most likely for SVGs, deliver minimal output
  if (img.sizes.length === 1) {
    return (
      <img
        className={classes.length > 0 ? classes.join(" ") : null}
        src={img.sizes[0][0]}
        width={img.w}
        height={img.h}
        alt={img.alt}
      />
    );
  }

  switch (columns) {
    case 6:
      sizesString =
        "(max-width: 640px) calc(100vw - 40px), (max-width: 1470px) calc((100vw - 90px) / 2), 690px";
      break;
    case 4:
      sizesString =
        "(max-width: 640px) calc(100vw - 40px), (max-width: 1470px) calc((100vw - 120px) / 3), 450px";
      break;
    default:
      // 12 column
      sizesString = "(max-width: 1470px) calc(100vw - 60px), 1410px";
      break;
  }

  for (var i = img.sizes.length - 1; i >= 0; i--) {
    srcSetStrings.push(`${img.sizes[i][0]} ${img.sizes[i][1]}w`);
  }

  return (
    <img
      className={classes.length > 0 ? classes.join(" ") : null}
      src={img.sizes[img.sizes.length - 1][0]}
      width={img.w}
      height={img.h}
      srcSet={srcSetStrings.join(", ")}
      sizes={sizesString}
      alt={img.alt}
    />
  );
};

// Various polyfills and browser detection functions that need to be run on startup
export const startupFunctions = () => {
  const body = document.body;

  if (detectIE()) {
    body.classList.add("ie");
  } else {
    body.classList.add("notIE");
  }

  if (detectChrome()) {
    body.classList.add("chrome");
  }

  if (detectSafari()) {
    body.classList.add("safari");
  }

  // Polyfill for MS Edge
  if (window.NodeList && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = function (callback, thisArg) {
      thisArg = thisArg || window;
      for (var i = 0; i < this.length; i++) {
        callback.call(thisArg, this[i], i, this);
      }
    };
  }

  // adds a highlight color (red, green, or blue) to the page, also the favicon
  const classes = ["chermayeff", "geismar", "haviv"];
  var randomClass = classes[Math.floor(Math.random() * classes.length)];
  var link = document.querySelector("link[rel*='icon']") || document.createElement("link");

  body.classList.add(randomClass);

  link.type = "image/png";
  link.rel = "icon";
  link.sizes = "16x16 32x32";
  link.href = `/img/favicon-${randomClass}.png`;

  document.getElementsByTagName("head")[0].appendChild(link);
};



/** WEBPACK FOOTER **
 ** ./src/helpers.js
 **/